import type { IRawTiledMap } from './tiled.types';

type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null;
}

function requireNumber(value: unknown, propertyName: string): void {
  if (typeof value !== 'number') {
    throw new Error(`Tiled map missing required numeric property: ${propertyName}`);
  }
}

function requireString(value: unknown, propertyName: string): void {
  if (typeof value !== 'string') {
    throw new Error(`Tiled map missing required string property: ${propertyName}`);
  }
}

function validateRawLayer(layer: unknown, path: string): void {
  if (!isRecord(layer)) {
    throw new Error(`Tiled map layer at ${path} must be an object`);
  }

  requireString(layer.name, `${path}.name`);
  requireString(layer.type, `${path}.type`);

  if (layer.type === 'tilelayer' && !Array.isArray(layer.data)) {
    throw new Error(`Tiled tile layer "${layer.name}" must include a data array`);
  }

  if (layer.type === 'objectgroup' && !Array.isArray(layer.objects)) {
    throw new Error(`Tiled object layer "${layer.name}" must include an objects array`);
  }

  if (layer.type === 'imagelayer') {
    requireString(layer.image, `${path}.image`);
  }

  if (layer.type === 'group') {
    const childLayers = layer.layers;
    if (!Array.isArray(childLayers)) {
      throw new Error(`Tiled group layer "${layer.name}" must include child layers as an array`);
    }

    for (let i = 0; i < childLayers.length; i++) {
      validateRawLayer(childLayers[i], `${path}.layers[${i}]`);
    }
    return;
  }

  if (layer.type !== 'tilelayer' && layer.type !== 'objectgroup' && layer.type !== 'imagelayer') {
    throw new Error(`Unsupported Tiled layer type at ${path}: ${layer.type}`);
  }
}

export function assertRawTiledMap(input: unknown): asserts input is IRawTiledMap {
  if (!isRecord(input)) {
    throw new Error('Tiled map data must be an object');
  }

  requireNumber(input.compressionlevel, 'compressionlevel');
  requireNumber(input.width, 'width');
  requireNumber(input.height, 'height');
  if (typeof input.infinite !== 'boolean') {
    throw new Error('Tiled map missing required boolean property: infinite');
  }
  requireNumber(input.nextlayerid, 'nextlayerid');
  requireNumber(input.nextobjectid, 'nextobjectid');
  requireString(input.orientation, 'orientation');
  requireString(input.renderorder, 'renderorder');
  requireString(input.tiledversion, 'tiledversion');
  requireNumber(input.tilewidth, 'tilewidth');
  requireNumber(input.tileheight, 'tileheight');
  if (input.type !== 'map') {
    throw new Error('Tiled map data must have type "map"');
  }

  if (typeof input.version !== 'string' && typeof input.version !== 'number') {
    throw new Error('Tiled map missing required version property');
  }

  if (!Array.isArray(input.tilesets)) {
    throw new Error('Tiled map must include a tilesets array');
  }

  if (!Array.isArray(input.layers)) {
    throw new Error('Raw Tiled map data must include layers as an array');
  }

  for (let i = 0; i < input.layers.length; i++) {
    validateRawLayer(input.layers[i], `layers[${i}]`);
  }
}

export function validateRawTiledMap(input: unknown): IRawTiledMap {
  assertRawTiledMap(input);
  return input;
}
